import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Bell, Image as ImageIcon, Images, Mic, MessageCircle } from "lucide-react";
import VoiceOrb from "@/components/VoiceOrb";

const PORTRAIT = "https://images.unsplash.com/photo-1619896382906-4f4681290afc?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const GALLERY = "https://images.unsplash.com/photo-1786325492185-20d00ed24e51?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const tools = [
  { icon: MessageCircle, title: "Conversation", text: "Discutez avec Sem.IA par écrit, elle répond en temps réel.", span: "lg:col-span-2" },
  { icon: Mic, title: "Voix", text: "Parlez-lui au micro, elle vous répond avec sa voix naturelle.", span: "" },
  { icon: ImageIcon, title: "Images", text: "Décrivez une idée, Sem.IA la transforme en image.", span: "" },
  { icon: Images, title: "Galerie Photos", text: "Retrouvez, téléchargez et gérez toutes vos créations.", span: "" },
  { icon: Bell, title: "Notifications", text: "Soyez prévenu quand Sem.IA a terminé une création.", span: "lg:col-span-2" },
];

const prompts = ["Raconte-moi une histoire courte", "Aide-moi à organiser ma journée", "Donne-moi une idée de recette"];

export default function Home() {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const go = (text) => nav(`/studio?q=${encodeURIComponent(text)}`);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <section className="grid lg:grid-cols-12 gap-10 pt-12 sm:pt-20 pb-20 items-center">
        <div className="lg:col-span-7 fade-up">
          <span className="label-mono text-[var(--accent)]">Compagne IA · Voix · Création</span>
          <h1 data-testid="home-hero-title" className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mt-5 tracking-tight">
            Rencontrez <em className="text-[var(--accent)]">Sem.IA</em>,<br />celle qui vous écoute<br />et vous répond.
          </h1>
          <p className="text-lg text-[var(--text-secondary)] mt-6 max-w-xl">Parlez-lui, écrivez-lui, demandez-lui des images. Sem.IA réunit tous vos outils créatifs dans un seul endroit, avec une voix chaleureuse.</p>
          <form onSubmit={(e) => { e.preventDefault(); q.trim() && go(q); }} className="mt-8 flex items-center gap-2 p-2 card max-w-xl" style={{ borderRadius: 999 }}>
            <input data-testid="home-quick-prompt-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Posez une question à Sem.IA…" className="flex-1 bg-transparent outline-none px-4 py-2" />
            <button data-testid="home-quick-prompt-submit" className="btn-primary">Envoyer <ArrowRight size={16} /></button>
          </form>
          <div className="flex flex-wrap gap-2 mt-4">
            {prompts.map((p, i) => (
              <button key={p} data-testid={`home-sample-prompt-${i}`} onClick={() => go(p)} className="text-sm px-4 py-2 rounded-full bg-[var(--surface-subtle)] text-[var(--text-secondary)] hover:bg-[var(--text)] hover:text-[var(--bg)] transition-colors">{p}</button>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 relative fade-up" style={{ animationDelay: ".15s" }}>
          <div className="relative rounded-[32px] overflow-hidden grain aspect-[4/5]">
            <img src={PORTRAIT} alt="Sem.IA" className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-8 -left-6 card p-4 flex items-center gap-4 shadow-xl">
            <VoiceOrb state="speaking" size={90} />
            <div className="pr-3">
              <p className="font-medium">« Bonjour, je suis Sem.IA. »</p>
              <p className="text-sm text-[var(--text-muted)]">Prête à discuter</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <h2 className="font-serif text-4xl sm:text-5xl max-w-xl">Tous les outils, une seule compagne.</h2>
          <Link to="/studio" data-testid="home-open-studio-link" className="btn-ghost">Ouvrir le Studio <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {tools.map(({ icon: Icon, title, text, span }, i) => (
            <Link to="/studio" key={title} data-testid={`home-tool-card-${i}`} className={`card card-hover p-8 flex flex-col justify-between min-h-[220px] ${span}`}>
              <span className="w-12 h-12 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] flex items-center justify-center"><Icon size={20} /></span>
              <div className="mt-8">
                <h3 className="text-xl font-semibold">{title}</h3>
                <p className="text-[var(--text-secondary)] mt-2">{text}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 rounded-[32px] overflow-hidden aspect-square">
          <img src={GALLERY} alt="Galerie" className="w-full h-full object-cover" />
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <span className="label-mono text-[var(--text-muted)]">Comment ça marche</span>
          {["Appuyez sur le micro et parlez naturellement.", "Sem.IA comprend, réfléchit et vous répond à voix haute.", "Demandez une image : elle la crée et la range dans votre galerie."].map((s, i) => (
            <div key={i} className="flex gap-6 py-6 border-b border-[var(--border)]">
              <span className="font-serif text-3xl text-[var(--accent)]">0{i + 1}</span>
              <p className="text-lg pt-1">{s}</p>
            </div>
          ))}
          <Link to="/studio" data-testid="home-bottom-cta" className="btn-primary mt-8">Commencer maintenant <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
